import { nextTick, ref, watch } from "vue";
import { watchImmediate } from "@vueuse/core";
import { toBoolean } from "#ce/utils/props";

/**
 * Owns the menu's open state and reports its changes to consumers.
 *
 * @param {object} options
 * @param {() => unknown} options.open - the `open` attribute value.
 * @param {(event: string, ...args: any[]) => void} options.emit - the component's emit.
 * @param {() => void} options.returnFocus - hands focus back once the menu is closed.
 * @returns {{
 *   isActive: import("vue").Ref<boolean>,
 *   onPopupShown: () => void,
 *   onSurfaceClosed: () => Promise<void>,
 * }}
 */
export function useMenuOpenState({ open, emit, returnFocus }) {
  const isActive = ref(toBoolean(open()));

  function emitUpdateOpen(active) {
    emit("update-open", active);
  }

  function applyOpenAttribute() {
    isActive.value = toBoolean(open());
  }

  watch(isActive, emitUpdateOpen);
  watchImmediate(open, applyOpenAttribute);

  /**
   * Emits `open` once the popup is visible, which is when its content can take focus. `update-open`
   * fires earlier, when the open state changes. The bottom sheet moves focus itself and emits no `open`.
   */
  function onPopupShown() {
    emit("open");
  }

  /** Closes the menu after its surface closed, then hands focus back and emits `close`. */
  async function onSurfaceClosed() {
    isActive.value = false;
    // The native surface is closed; let its closed-state classes render before handoff.
    await nextTick();
    if (!isActive.value) {
      returnFocus();
      emit("close");
    }
  }

  return { isActive, onPopupShown, onSurfaceClosed };
}
